import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Search,
  User,
  Sparkles,
  Loader2,
  ChevronRight,
  ChevronLeft,
  Check,
  ShoppingCart,
  Wallet,
  AlertCircle,
  History,
  Printer,
  Share2,
  MessageCircle,
  Volume2,
  Plus,
  X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { TextToSpeech } from '../voice';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Constants
const SKIN_ZONES = [
  { id: 'face', label: 'Visage', icon: '😊' },
  { id: 'body', label: 'Corps', icon: '🧴' },
  { id: 'hands', label: 'Mains', icon: '🤲' }
];

const SKIN_TYPES = [
  { id: 'oily', label: 'Grasse' },
  { id: 'dry', label: 'Sèche' },
  { id: 'combination', label: 'Mixte' },
  { id: 'normal', label: 'Normale' }
];

const SKIN_CONCERNS = {
  face: [
    { id: 'acne', label: 'Acné' },
    { id: 'dark_spots', label: 'Taches sombres' },
    { id: 'wrinkles', label: 'Rides' },
    { id: 'redness', label: 'Rougeurs' },
    { id: 'dryness', label: 'Sécheresse' },
    { id: 'oiliness', label: 'Excès de sébum' },
    { id: 'sensitivity', label: 'Sensibilité' },
    { id: 'baby_skin', label: 'Peau de bébé' }
  ],
  body: [
    { id: 'dryness', label: 'Sécheresse' },
    { id: 'stretch_marks', label: 'Vergetures' },
    { id: 'cellulite', label: 'Cellulite' },
    { id: 'sensitivity', label: 'Sensibilité' },
    { id: 'baby_skin', label: 'Peau de bébé' }
  ],
  hands: [
    { id: 'dryness', label: 'Sécheresse' },
    { id: 'cracks', label: 'Crevasses' },
    { id: 'dark_spots', label: 'Taches' },
    { id: 'sensitivity', label: 'Sensibilité' }
  ]
};

const HAIR_TYPES = [
  { id: 'coily', label: 'Crépus' },
  { id: 'curly', label: 'Bouclés' },
  { id: 'wavy', label: 'Ondulés' },
  { id: 'straight', label: 'Lisses' }
];

const SCALP_CONDITIONS = [
  { id: 'normal', label: 'Normal' },
  { id: 'oily', label: 'Gras' },
  { id: 'dry', label: 'Sec' },
  { id: 'dandruff', label: 'Pellicules' }
];

const HAIR_CONDITIONS = [
  { id: 'normal', label: 'Normal' },
  { id: 'dry', label: 'Sec' },
  { id: 'brittle', label: 'Cassant' },
  { id: 'split_ends', label: 'Fourches' }
];

const HAIR_CONCERNS = [
  { id: 'hair_loss', label: 'Chute de cheveux' },
  { id: 'lack_volume', label: 'Manque de volume' },
  { id: 'itchiness', label: 'Démangeaisons' },
  { id: 'breakage', label: 'Casse' },
  { id: 'dullness', label: 'Cheveux ternes' },
  { id: 'frizz', label: 'Frisottis' }
];

const NAIL_CONCERNS_HANDS = [
  { id: 'brittle', label: 'Cassants' },
  { id: 'ridged', label: 'Striés' },
  { id: 'split', label: 'Dédoublés' },
  { id: 'fungus', label: 'Mycose' },
  { id: 'discolored', label: 'Décolorés' }
];

const NAIL_CONCERNS_FEET = [
  { id: 'brittle', label: 'Cassants' },
  { id: 'thick', label: 'Épaissis' },
  { id: 'fungus', label: 'Mycose' },
  { id: 'ingrown', label: 'Incarnés' }
];

const BUDGET_OPTIONS = [
  { id: 'under_10k', label: '< 10 000 FCFA' },
  { id: '10k_25k', label: '10 000 - 25 000 FCFA' },
  { id: '25k_50k', label: '25 000 - 50 000 FCFA' },
  { id: 'over_50k', label: '> 50 000 FCFA' },
  { id: null, label: 'Non précisé' }
];

const AGE_GROUPS = [
  { id: 'under_25', label: 'Moins de 25 ans' },
  { id: '25-35', label: '25 - 35 ans' },
  { id: '35-45', label: '35 - 45 ans' },
  { id: '45+', label: '45 ans et plus' }
];

const ComprehensiveDiagnosis = ({ language = 'fr', onComplete, onCancel, initialIdentification = null }) => {
  // Steps — skip step 1 (identification) if pre-filled from parent funnel
  const [step, setStep] = useState(initialIdentification ? 2 : 1);
  const [loading, setLoading] = useState(false);
  
  // Client info
  const [identMode, setIdentMode] = useState(initialIdentification?.mode || 'phone'); // 'phone' | 'code'
  const [clientPhone, setClientPhone] = useState(initialIdentification?.phone || '');
  const [clientCode, setClientCode] = useState(initialIdentification?.client_code || '');
  const [clientName, setClientName] = useState(initialIdentification?.first_name || '');
  const [clientAge, setClientAge] = useState('');
  const [clientBirthYear, setClientBirthYear] = useState(initialIdentification?.birth_year ? String(initialIdentification.birth_year) : '');
  const [clientSex, setClientSex] = useState('');
  const [clientAllergies, setClientAllergies] = useState('');
  const [clientTreatments, setClientTreatments] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [existingClient, setExistingClient] = useState(null);
  const [generatedCode, setGeneratedCode] = useState(null); // affiché après création
  const [codeLookupError, setCodeLookupError] = useState(null);
  
  // Budget
  const [budget, setBudget] = useState(null);
  
  // Areas to diagnose
  const [diagnoseSkin, setDiagnoseSkin] = useState(true);
  const [diagnoseHair, setDiagnoseHair] = useState(false);
  const [diagnoseNails, setDiagnoseNails] = useState(false);
  
  // Skin zones
  const [selectedSkinZones, setSelectedSkinZones] = useState(['face']);
  const [skinZoneData, setSkinZoneData] = useState({
    face: { skin_type: '', concerns: [] },
    body: { skin_type: '', concerns: [] },
    hands: { skin_type: '', concerns: [] }
  });
  const [ageGroup, setAgeGroup] = useState('');
  
  // Hair
  const [hairType, setHairType] = useState('');
  const [scalpCondition, setScalpCondition] = useState('');
  const [hairCondition, setHairCondition] = useState('');
  const [hairConcerns, setHairConcerns] = useState([]);
  
  // Nails
  const [nailsHandsConcerns, setNailsHandsConcerns] = useState([]);
  const [nailsFeetConcerns, setNailsFeetConcerns] = useState([]);
  
  // Results
  const [diagnosisResult, setDiagnosisResult] = useState(null);
  
  // Manual product search
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [manualProducts, setManualProducts] = useState([]);
  
  // Search client on phone input
  useEffect(() => {
    if (identMode !== 'phone') return;
    const searchClient = async () => {
      if (clientPhone.length >= 10) {
        try {
          const token = localStorage.getItem('dermascan_token');
          const response = await axios.get(`${API_URL}/comprehensive/client/${clientPhone}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.client) {
            setExistingClient(response.data);
            setClientName(`${response.data.client.first_name || ''} ${response.data.client.last_name || ''}`.trim());
            setClientAge(response.data.client.age?.toString() || '');
            setClientSex(response.data.client.sex || '');
            setClientAllergies(response.data.client.allergies || '');
            setClientTreatments(response.data.client.current_treatments || '');
            setClientNotes(response.data.client.notes || '');
          } else {
            setExistingClient(null);
          }
        } catch (error) {
          console.error('Error searching client:', error);
        }
      }
    };

    const timer = setTimeout(searchClient, 500);
    return () => clearTimeout(timer);
  }, [clientPhone, identMode]);

  // Recherche par code client (mode 'code')
  const searchClientByCode = async () => {
    if (!clientCode || clientCode.length < 5) {
      setCodeLookupError('Code trop court (format SK-XXXX)');
      return;
    }
    setCodeLookupError(null);
    try {
      const token = localStorage.getItem('dermascan_token');
      const params = new URLSearchParams();
      if (clientName) params.set('first_name', clientName.split(' ')[0]);
      if (clientBirthYear) params.set('birth_year', clientBirthYear);
      const url = `${API_URL}/comprehensive/client/by-code/${encodeURIComponent(clientCode.toUpperCase())}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.client) {
        setExistingClient(response.data);
        const c = response.data.client;
        setClientName(`${c.first_name || ''} ${c.last_name || ''}`.trim());
        setClientAge(c.age?.toString() || '');
        setClientBirthYear(c.birth_year?.toString() || '');
        setClientSex(c.sex || '');
        setClientAllergies(c.allergies || '');
        setClientTreatments(c.current_treatments || '');
        setClientNotes(c.notes || '');
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Code introuvable';
      setCodeLookupError(msg);
      setExistingClient(null);
    }
  };
  
  const toggleSkinZone = (zoneId) => {
    setSelectedSkinZones(prev => 
      prev.includes(zoneId) 
        ? prev.filter(z => z !== zoneId)
        : [...prev, zoneId]
    );
  };
  
  // Search products for manual addition
  const searchProductsManual = async (query) => {
    if (!query || query.length < 2) {
      setProductSearchResults([]);
      return;
    }
    
    setSearchingProducts(true);
    try {
      const token = localStorage.getItem('dermascan_token');
      const response = await axios.get(`${API_URL}/comprehensive/products/search?q=${encodeURIComponent(query)}&limit=15`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductSearchResults(response.data || []);
    } catch (error) {
      console.error('Error searching products:', error);
      setProductSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  };
  
  // Debounced product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchQuery.length >= 2) {
        searchProductsManual(productSearchQuery);
      } else {
        setProductSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchQuery]);
  
  // Add manual product
  const addManualProduct = (product) => {
    const exists = manualProducts.some(p => (p.code || p.name) === (product.code || product.name));
    if (exists) {
      toast.info('Ce produit est déjà ajouté');
      return;
    }
    setManualProducts(prev => [...prev, product]);
    setProductSearchQuery('');
    setProductSearchResults([]);
    toast.success(`${product.name} ajouté`);
  };
  
  // Remove manual product
  const removeManualProduct = (index) => {
    setManualProducts(prev => prev.filter((_, i) => i !== index));
  };
  
  const updateSkinZoneType = (zoneId, skinType) => {
    setSkinZoneData(prev => ({
      ...prev,
      [zoneId]: { ...prev[zoneId], skin_type: skinType }
    }));
  };
  
  const toggleSkinZoneConcern = (zoneId, concernId) => {
    setSkinZoneData(prev => ({
      ...prev,
      [zoneId]: {
        ...prev[zoneId],
        concerns: prev[zoneId].concerns.includes(concernId)
          ? prev[zoneId].concerns.filter(c => c !== concernId)
          : [...prev[zoneId].concerns, concernId]
      }
    }));
  };
  
  const toggleHairConcern = (concernId) => {
    setHairConcerns(prev => 
      prev.includes(concernId) 
        ? prev.filter(c => c !== concernId)
        : [...prev, concernId]
    );
  };
  
  const submitDiagnosis = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('dermascan_token');
      
      // Build skin zones array
      const skinZones = selectedSkinZones.map(zoneId => ({
        zone: zoneId,
        skin_type: skinZoneData[zoneId].skin_type,
        concerns: skinZoneData[zoneId].concerns
      }));
      
      const payload = {
        client_phone: identMode === 'phone' ? clientPhone : null,
        client_code: identMode === 'code' ? (clientCode || null) : null,
        client_name: clientName,
        budget: budget,
        diagnose_skin: diagnoseSkin,
        diagnose_hair: diagnoseHair,
        diagnose_nails: diagnoseNails,
        skin_zones: diagnoseSkin ? skinZones : [],
        age_group: ageGroup,
        hair: diagnoseHair ? {
          hair_type: hairType,
          scalp_condition: scalpCondition,
          hair_condition: hairCondition,
          concerns: hairConcerns
        } : null,
        nails: diagnoseNails ? {
          hands_concerns: nailsHandsConcerns,
          feet_concerns: nailsFeetConcerns
        } : null
      };
      
      // First, save/update client profile
      const profileResp = await axios.post(`${API_URL}/comprehensive/client/profile`, {
        phone: identMode === 'phone' ? clientPhone : null,
        client_code: identMode === 'code' ? (clientCode || null) : null,
        first_name: clientName.split(' ')[0] || clientName,
        last_name: clientName.split(' ').slice(1).join(' ') || null,
        age: clientAge ? parseInt(clientAge) : null,
        birth_year: clientBirthYear ? parseInt(clientBirthYear) : null,
        sex: clientSex || null,
        allergies: clientAllergies || null,
        current_treatments: clientTreatments || null,
        notes: clientNotes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Si un code a été généré pour ce nouveau client → on le mémorise + on l'injecte dans le payload du diagnostic
      const newCode = profileResp.data?.generated_client_code || profileResp.data?.client?.client_code;
      if (newCode && identMode === 'code' && !clientCode) {
        setClientCode(newCode);
        setGeneratedCode(newCode);
        payload.client_code = newCode;
      }

      // Then create diagnosis
      const response = await axios.post(`${API_URL}/comprehensive/diagnosis`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setDiagnosisResult(response.data);
      setStep(5); // Go to results
      
    } catch (error) {
      console.error('Error creating diagnosis:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du diagnostic');
    } finally {
      setLoading(false);
    }
  };
  
  const canProceedStep1 = (
    (identMode === 'phone' ? clientPhone.length >= 10 : true) &&
    clientName.trim()
  );
  const canProceedStep2 = diagnoseSkin || diagnoseHair || diagnoseNails;
  const canProceedStep3 = () => {
    if (diagnoseSkin && selectedSkinZones.length === 0) return false;
    if (diagnoseSkin) {
      for (const zone of selectedSkinZones) {
        if (!skinZoneData[zone].skin_type) return false;
      }
    }
    return true;
  };
  const canProceedStep4 = () => {
    if (diagnoseHair && (!scalpCondition || !hairCondition)) return false;
    return true;
  };
  
  // Step 1: Client Info
  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <User className="w-12 h-12 mx-auto text-#2E7D32 mb-2" />
        <h2 className="text-xl font-bold">Informations Client</h2>
        <p className="text-sm text-gray-500">Étape 1/4</p>
      </div>
      
      {/* Toggle d'identification : Téléphone (recommandé) / Code client */}
      <div className="bg-zinc-50 border-2 border-zinc-200 rounded-lg p-3">
        <p className="text-xs uppercase tracking-wide text-zinc-500 font-semibold mb-2">
          Mode d'identification
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => { setIdentMode('phone'); setExistingClient(null); setCodeLookupError(null); }}
            data-testid="ident-mode-phone"
            className={`flex-1 min-h-[48px] px-3 py-2 rounded-lg font-semibold text-sm border-2 transition active:scale-95 ${
              identMode === 'phone'
                ? 'bg-[#2E7D32] text-white border-[#2E7D32] shadow'
                : 'bg-white text-zinc-700 border-zinc-200'
            }`}
          >
            📱 Téléphone <span className="text-xs opacity-80">(recommandé)</span>
          </button>
          <button
            type="button"
            onClick={() => { setIdentMode('code'); setExistingClient(null); setClientPhone(''); }}
            data-testid="ident-mode-code"
            className={`flex-1 min-h-[48px] px-3 py-2 rounded-lg font-semibold text-sm border-2 transition active:scale-95 ${
              identMode === 'code'
                ? 'bg-[#5B3B8C] text-white border-[#5B3B8C] shadow'
                : 'bg-white text-zinc-700 border-zinc-200'
            }`}
          >
            🔑 Code client
          </button>
        </div>
        <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          {identMode === 'phone'
            ? 'Le numéro de téléphone permet le suivi WhatsApp et les notifications.'
            : 'Pour les clients qui ne souhaitent pas donner leur téléphone. Un code court sera généré (ex: SK-K7M3) à conserver.'}
        </p>
      </div>

      {/* Phone (mode téléphone) */}
      {identMode === 'phone' && (
      <div>
        <Label>Téléphone du client *</Label>
        <Input
          type="tel"
          inputMode="tel"
          placeholder="07 XX XX XX XX"
          value={clientPhone}
          onChange={(e) => setClientPhone(e.target.value)}
          className="text-lg h-12"
          data-testid="client-phone-input"
        />
      </div>
      )}

      {/* Code client (mode code) */}
      {identMode === 'code' && (
      <div className="space-y-3">
        <div>
          <Label>Code client (laisser vide pour un nouveau client)</Label>
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="SK-XXXX"
              value={clientCode}
              onChange={(e) => setClientCode(e.target.value.toUpperCase())}
              className="text-lg h-12 font-mono uppercase tracking-wider flex-1"
              maxLength={7}
              data-testid="client-code-input"
            />
            <Button
              type="button"
              onClick={searchClientByCode}
              disabled={!clientCode || clientCode.length < 5}
              className="h-12 bg-[#5B3B8C] text-white hover:bg-[#4A2F75] font-semibold px-4"
              data-testid="client-code-search-btn"
            >
              🔍 Chercher
            </Button>
          </div>
          {codeLookupError && (
            <p className="text-xs text-red-600 mt-1">{codeLookupError}</p>
          )}
          <p className="text-[11px] text-zinc-500 mt-1">
            Si le client n'a jamais été enregistré, laissez vide → un code unique sera généré à la création.
          </p>
        </div>
        <div>
          <Label>Année de naissance <span className="text-zinc-400 font-normal">(facultatif, sécurise la recherche par code)</span></Label>
          <Input
            type="number"
            inputMode="numeric"
            placeholder="ex: 1985"
            value={clientBirthYear}
            onChange={(e) => setClientBirthYear(e.target.value)}
            className="text-base h-12"
            min="1900"
            max="2030"
            data-testid="client-birth-year-input"
          />
        </div>
      </div>
      )}

      {/* Bandeau code généré (après création nouveau client sans téléphone) */}
      {generatedCode && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4" data-testid="generated-code-banner">
          <p className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-1">
            ⚠️ Code client à communiquer au client
          </p>
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono font-bold text-2xl sm:text-3xl text-amber-900 tracking-wider select-all">
              {generatedCode}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="h-10 border-amber-300 text-amber-700"
              onClick={() => {
                try {
                  navigator.clipboard.writeText(generatedCode);
                } catch {/* ignore */}
              }}
            >
              📋 Copier
            </Button>
          </div>
          <Button
            type="button"
            className="mt-3 w-full h-12 bg-[#0F5947] hover:bg-[#0a3d31] text-white font-semibold"
            data-testid="print-client-card-btn"
            onClick={async () => {
              try {
                const token = localStorage.getItem('dermascan_token');
                const r = await axios.get(`${API_URL}/comprehensive/client/card/${generatedCode}`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: 'blob',
                });
                const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                const w = window.open(blobUrl, '_blank');
                if (!w) {
                  const a = document.createElement('a');
                  a.href = blobUrl;
                  a.download = `carte_${generatedCode}.pdf`;
                  a.click();
                }
              } catch (e) {
                toast.error('Erreur génération carte');
              }
            }}
          >
            🖨 Imprimer la carte client (85×54 mm)
          </Button>
          <p className="text-xs text-amber-700 mt-2 leading-relaxed">
            ✍️ Notez ce code sur un papier OU imprimez la carte chic ci-dessus à donner au client.
          </p>
        </div>
      )}

      {/* Existing client indicator */}
      {existingClient?.client && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-green-700">
            <Check className="w-4 h-4" />
            <span className="font-medium">Client existant</span>
          </div>
          <p className="text-sm text-green-600 mt-1">
            {existingClient.stats.total_diagnoses} diagnostic(s) • 
            {existingClient.stats.total_purchases} achat(s) • 
            {existingClient.stats.total_spent?.toLocaleString('fr-FR')} FCFA dépensés
          </p>
        </div>
      )}

      {/* Client History - Previous diagnoses */}
      {existingClient?.comprehensive_diagnoses?.length > 0 && (
        <Card className="border-#AED581 bg-#DCEDC8/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-#558B2F">
              <History className="w-4 h-4" />
              Diagnostics précédents
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-48 overflow-y-auto space-y-2">
            {existingClient.comprehensive_diagnoses.slice(0, 3).map((diag, idx) => (
              <div key={idx} className="bg-white rounded p-2 border border-#C5E1A5">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-500">
                      {new Date(diag.created_at).toLocaleDateString('fr-FR')} par {diag.agent_name}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {diag.skin_results && <Badge variant="outline" className="text-xs bg-#E8F5E9">🧴 Peau</Badge>}
                      {diag.hair_results && <Badge variant="outline" className="text-xs bg-#DCEDC8">💇 Cheveux</Badge>}
                      {diag.nails_results && <Badge variant="outline" className="text-xs bg-#E8F5E9">💅 Ongles</Badge>}
                    </div>
                  </div>
                  <span className="text-sm font-medium text-#558B2F">
                    {diag.total_recommended_value?.toLocaleString('fr-FR')} FCFA
                  </span>
                </div>
                {diag.recommendations?.length > 0 && (
                  <div className="mt-2 text-xs text-gray-600">
                    <span className="font-medium">Produits recommandés:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {diag.recommendations.slice(0, 2).map((rec, rIdx) => (
                        <span key={rIdx} className="bg-gray-100 px-1 rounded">
                          {rec.zone_label}: {rec.products?.length || 0} produits
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Client History - Previous purchases */}
      {existingClient?.sales?.length > 0 && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-green-700">
              <ShoppingCart className="w-4 h-4" />
              Achats précédents
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-40 overflow-y-auto space-y-2">
            {existingClient.sales.slice(0, 3).map((sale, idx) => (
              <div key={idx} className="bg-white rounded p-2 border border-green-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs text-gray-500">
                      {new Date(sale.created_at).toLocaleDateString('fr-FR')} • Ticket #{sale.ticket_mediciel}
                    </p>
                    <div className="text-xs text-gray-600 mt-1">
                      {(sale.products?.length || 0) + (sale.supplements?.length || 0)} produits
                    </div>
                  </div>
                  <span className="text-sm font-medium text-green-700">
                    {sale.total_amount?.toLocaleString('fr-FR')} FCFA
                  </span>
                </div>
                {sale.products?.length > 0 && (
                  <div className="mt-1 text-xs text-gray-500">
                    {sale.products.slice(0, 2).map(p => p.name).join(', ')}
                    {sale.products.length > 2 && '...'}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* Name */}
      <div>
        <Label>Nom du client *</Label>
        <Input
          placeholder="Prénom Nom"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
        />
      </div>
      
      {/* Age & Sex */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Âge</Label>
          <Input
            type="number"
            placeholder="Ex: 35"
            value={clientAge}
            onChange={(e) => setClientAge(e.target.value)}
          />
        </div>
        <div>
          <Label>Sexe</Label>
          <Select value={clientSex} onValueChange={setClientSex}>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="female">Femme</SelectItem>
              <SelectItem value="male">Homme</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Allergies */}
      <div>
        <Label>Allergies connues</Label>
        <Textarea
          placeholder="Ex: Allergique au parfum, parabènes..."
          value={clientAllergies}
          onChange={(e) => setClientAllergies(e.target.value)}
          rows={2}
        />
      </div>
      
      {/* Current treatments */}
      <div>
        <Label>Traitements en cours</Label>
        <Textarea
          placeholder="Ex: Traitement acné, crème cortisone..."
          value={clientTreatments}
          onChange={(e) => setClientTreatments(e.target.value)}
          rows={2}
        />
      </div>
      
      {/* Budget */}
      <div>
        <Label className="flex items-center gap-2">
          <Wallet className="w-4 h-4" />
          Budget du client (optionnel)
        </Label>
        <Select value={budget || ''} onValueChange={(v) => setBudget(v || null)}>
          <SelectTrigger>
            <SelectValue placeholder="Non précisé" />
          </SelectTrigger>
          <SelectContent>
            {BUDGET_OPTIONS.map(opt => (
              <SelectItem key={opt.id || 'none'} value={opt.id || 'none'}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
  
  // Step 2: Select areas
  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <Sparkles className="w-12 h-12 mx-auto text-#2E7D32 mb-2" />
        <h2 className="text-xl font-bold">Zones à analyser</h2>
        <p className="text-sm text-gray-500">Étape 2/4 - Sélectionnez les préoccupations</p>
      </div>
      
      {/* Skin */}
      <Card className={`cursor-pointer transition-all ${diagnoseSkin ? 'ring-2 ring-#2E7D32 bg-#E8F5E9' : ''}`}
        onClick={() => setDiagnoseSkin(!diagnoseSkin)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">🧴</span>
              <div>
                <h3 className="font-semibold">Peau</h3>
                <p className="text-sm text-gray-500">Visage, corps, mains</p>
              </div>
            </div>
            <Checkbox checked={diagnoseSkin} />
          </div>
        </CardContent>
      </Card>
      
      {/* Hair */}
      <Card className={`cursor-pointer transition-all ${diagnoseHair ? 'ring-2 ring-#2E7D32 bg-#E8F5E9' : ''}`}
        onClick={() => setDiagnoseHair(!diagnoseHair)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">💇</span>
              <div>
                <h3 className="font-semibold">Cheveux</h3>
                <p className="text-sm text-gray-500">Cuir chevelu, fibre capillaire</p>
              </div>
            </div>
            <Checkbox checked={diagnoseHair} />
          </div>
        </CardContent>
      </Card>
      
      {/* Nails */}
      <Card className={`cursor-pointer transition-all ${diagnoseNails ? 'ring-2 ring-#2E7D32 bg-#E8F5E9' : ''}`}
        onClick={() => setDiagnoseNails(!diagnoseNails)}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">💅</span>
              <div>
                <h3 className="font-semibold">Ongles</h3>
                <p className="text-sm text-gray-500">Mains et pieds</p>
              </div>
            </div>
            <Checkbox checked={diagnoseNails} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
  
  // Step 3: Skin analysis
  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <span className="text-4xl">🧴</span>
        <h2 className="text-xl font-bold">Diagnostic Peau</h2>
        <p className="text-sm text-gray-500">Étape 3/4</p>
      </div>
      
      {/* Zone selection */}
      <div>
        <Label className="mb-2 block">Zones concernées</Label>
        <div className="flex flex-wrap gap-2">
          {SKIN_ZONES.map(zone => (
            <Button
              key={zone.id}
              variant={selectedSkinZones.includes(zone.id) ? 'default' : 'outline'}
              onClick={() => toggleSkinZone(zone.id)}
              className="flex items-center gap-2"
            >
              <span>{zone.icon}</span>
              {zone.label}
            </Button>
          ))}
        </div>
      </div>
      
      {/* Per-zone analysis */}
      {selectedSkinZones.map(zoneId => {
        const zone = SKIN_ZONES.find(z => z.id === zoneId);
        return (
          <Card key={zoneId} className="border-l-4 border-l-#2E7D32">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <span>{zone?.icon}</span> {zone?.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Skin type */}
              <div>
                <Label className="text-sm">Type de peau</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {SKIN_TYPES.map(type => (
                    <Button
                      key={type.id}
                      size="sm"
                      variant={skinZoneData[zoneId]?.skin_type === type.id ? 'default' : 'outline'}
                      onClick={() => updateSkinZoneType(zoneId, type.id)}
                    >
                      {type.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Concerns */}
              <div>
                <Label className="text-sm">Préoccupations</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {SKIN_CONCERNS[zoneId]?.map(concern => (
                    <Badge
                      key={concern.id}
                      variant={skinZoneData[zoneId]?.concerns.includes(concern.id) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleSkinZoneConcern(zoneId, concern.id)}
                    >
                      {concern.label}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {/* Age group */}
      <div>
        <Label>Groupe d'âge (pour les produits anti-âge)</Label>
        <Select value={ageGroup} onValueChange={setAgeGroup}>
          <SelectTrigger>
            <SelectValue placeholder="Sélectionner" />
          </SelectTrigger>
          <SelectContent>
            {AGE_GROUPS.map(ag => (
              <SelectItem key={ag.id} value={ag.id}>{ag.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
  
  // Step 4: Hair & Nails
  const renderStep4 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <h2 className="text-xl font-bold">Diagnostic Cheveux & Ongles</h2>
        <p className="text-sm text-gray-500">Étape 4/4</p>
      </div>
      
      {/* Hair */}
      {diagnoseHair && (
        <Card className="border-l-4 border-l-#7CB342">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <span>💇</span> Cheveux
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Hair type */}
            <div>
              <Label className="text-sm">Type de cheveux</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {HAIR_TYPES.map(type => (
                  <Button
                    key={type.id}
                    size="sm"
                    variant={hairType === type.id ? 'default' : 'outline'}
                    onClick={() => setHairType(type.id)}
                  >
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Scalp */}
            <div>
              <Label className="text-sm">État du cuir chevelu *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {SCALP_CONDITIONS.map(cond => (
                  <Button
                    key={cond.id}
                    size="sm"
                    variant={scalpCondition === cond.id ? 'default' : 'outline'}
                    onClick={() => setScalpCondition(cond.id)}
                  >
                    {cond.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Hair condition */}
            <div>
              <Label className="text-sm">État de la fibre *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {HAIR_CONDITIONS.map(cond => (
                  <Button
                    key={cond.id}
                    size="sm"
                    variant={hairCondition === cond.id ? 'default' : 'outline'}
                    onClick={() => setHairCondition(cond.id)}
                  >
                    {cond.label}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Concerns */}
            <div>
              <Label className="text-sm">Préoccupations</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {HAIR_CONCERNS.map(concern => (
                  <Badge
                    key={concern.id}
                    variant={hairConcerns.includes(concern.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleHairConcern(concern.id)}
                  >
                    {concern.label}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Nails */}
      {diagnoseNails && (
        <Card className="border-l-4 border-l-#66BB6A">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <span>💅</span> Ongles
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Hands */}
            <div>
              <Label className="text-sm">Ongles des mains</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {NAIL_CONCERNS_HANDS.map(concern => (
                  <Badge
                    key={concern.id}
                    variant={nailsHandsConcerns.includes(concern.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setNailsHandsConcerns(prev => 
                      prev.includes(concern.id) 
                        ? prev.filter(c => c !== concern.id)
                        : [...prev, concern.id]
                    )}
                  >
                    {concern.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Feet */}
            <div>
              <Label className="text-sm">Ongles des pieds</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {NAIL_CONCERNS_FEET.map(concern => (
                  <Badge
                    key={concern.id}
                    variant={nailsFeetConcerns.includes(concern.id) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setNailsFeetConcerns(prev => 
                      prev.includes(concern.id) 
                        ? prev.filter(c => c !== concern.id)
                        : [...prev, concern.id]
                    )}
                  >
                    {concern.label}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {!diagnoseHair && !diagnoseNails && (
        <div className="text-center py-8 text-gray-500">
          <p>Aucun diagnostic cheveux/ongles sélectionné</p>
          <Button variant="outline" className="mt-2" onClick={() => setStep(2)}>
            Modifier la sélection
          </Button>
        </div>
      )}
    </div>
  );
  
  // Step 5: Results
  const renderStep5 = () => (
    <div className="space-y-4">
      <div className="text-center mb-4">
        <Check className="w-12 h-12 mx-auto text-green-600 mb-2" />
        <h2 className="text-xl font-bold">Recommandations</h2>
        <p className="text-sm text-gray-500">Pour {clientName}</p>
      </div>
      
      {/* Budget indicator */}
      {diagnosisResult?.budget && (
        <div className={`p-3 rounded-lg ${diagnosisResult.within_budget ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center gap-2">
            {diagnosisResult.within_budget ? (
              <Check className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-600" />
            )}
            <span className={diagnosisResult.within_budget ? 'text-green-700' : 'text-amber-700'}>
              Budget: {BUDGET_OPTIONS.find(b => b.id === diagnosisResult.budget)?.label}
              {!diagnosisResult.within_budget && ' - Total dépasse le budget'}
            </span>
          </div>
        </div>
      )}
      
      {/* Recommendations by zone */}
      {diagnosisResult?.recommendations?.map((rec, index) => (
        <Card key={index} className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-#2E7D32 to-#43A047 text-white py-3">
            <CardTitle className="text-lg">{rec.zone_label}</CardTitle>
            <div className="flex flex-wrap gap-1 mt-1">
              {rec.concerns?.map(c => (
                <Badge key={c} variant="secondary" className="bg-white/20 text-white text-xs">
                  {c}
                </Badge>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {rec.products?.length > 0 ? (
              <div className="divide-y">
                {rec.products.map((product, pIndex) => (
                  <div key={pIndex} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{product.name}</p>
                      {product.indication && (
                        <p className="text-xs text-gray-500">{product.indication}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-#1B5E20">
                        {product.price?.toLocaleString('fr-FR')} FCFA
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="p-4 text-center text-gray-500 text-sm">
                Aucun produit trouvé pour ces critères
              </p>
            )}
          </CardContent>
        </Card>
      ))}
      
      {/* Manual Product Search Section */}
      <Card className="border-dashed border-2 border-#81C784">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-#2E7D32" />
              Ajouter un produit manuellement
            </span>
            {!showProductSearch && (
              <Button 
                size="sm" 
                variant="outline"
                className="text-xs"
                onClick={() => setShowProductSearch(true)}
              >
                Rechercher
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        
        {showProductSearch && (
          <CardContent className="pt-0">
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Rechercher un produit (ex: Château Rouge, bébé...)"
                  value={productSearchQuery}
                  onChange={(e) => setProductSearchQuery(e.target.value)}
                  className="pl-9"
                />
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => {
                    setShowProductSearch(false);
                    setProductSearchQuery('');
                    setProductSearchResults([]);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              {searchingProducts && (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  <span className="text-sm text-gray-500">Recherche...</span>
                </div>
              )}
              
              {!searchingProducts && productSearchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {productSearchResults.map((product, idx) => (
                    <div 
                      key={idx}
                      className="p-2 hover:bg-#E8F5E9 cursor-pointer flex items-center justify-between"
                      onClick={() => addManualProduct(product)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{product.name}</p>
                        <p className="text-xs text-gray-500">
                          {product.brand || product.category || product.source}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-#2E7D32">
                          {product.price?.toLocaleString('fr-FR')} FCFA
                        </span>
                        <Plus className="w-4 h-4 text-#2E7D32" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {!searchingProducts && productSearchQuery.length >= 2 && productSearchResults.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-2">Aucun produit trouvé</p>
              )}
            </div>
          </CardContent>
        )}
        
        {/* Manual products added */}
        {manualProducts.length > 0 && (
          <CardContent className="pt-0">
            <p className="text-xs font-medium text-#1B5E20 mb-2">Produits ajoutés manuellement :</p>
            <div className="space-y-2">
              {manualProducts.map((product, idx) => (
                <div key={idx} className="flex items-center justify-between bg-#E8F5E9 p-2 rounded">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{product.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-#2E7D32">
                      {product.price?.toLocaleString('fr-FR')} FCFA
                    </span>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-6 w-6 p-0 text-red-500 hover:bg-red-100"
                      onClick={() => removeManualProduct(idx)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
      
      {/* Total */}
      <Card className="bg-#E8F5E9 border-#A5D6A7">
        <CardContent className="p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="font-medium">Total estimé</span>
            <span className="text-2xl font-bold text-#1B5E20">
              {((diagnosisResult?.total_recommended_value || 0) + manualProducts.reduce((sum, p) => sum + (p.price || 0), 0)).toLocaleString('fr-FR')} FCFA
            </span>
          </div>
          
          {/* Unique products count */}
          {(diagnosisResult?.unique_products_count || manualProducts.length > 0) && (
            <p className="text-sm text-#2E7D32">
              {(diagnosisResult?.unique_products_count || 0) + manualProducts.length} produit{((diagnosisResult?.unique_products_count || 0) + manualProducts.length) > 1 ? 's' : ''}
              {manualProducts.length > 0 && ` (dont ${manualProducts.length} ajouté${manualProducts.length > 1 ? 's' : ''} manuellement)`}
            </p>
          )}
          
          {/* Multi-zone products info */}
          {diagnosisResult?.multi_zone_products?.length > 0 && (
            <div className="bg-white/60 rounded-lg p-3 border border-#A5D6A7">
              <p className="text-xs font-medium text-#1B5E20 mb-2">💡 Produits multi-usages :</p>
              {diagnosisResult.multi_zone_products.map((mp, idx) => (
                <p key={idx} className="text-xs text-#2E7D32">
                  • {mp.name} → traite {mp.zones?.join(', ')}
                </p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Share & Print Actions */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          className="flex-1 border-green-500 text-green-600 hover:bg-green-50"
          onClick={shareViaWhatsApp}
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>
        <Button 
          variant="outline" 
          className="flex-1 border-blue-500 text-blue-600 hover:bg-blue-50"
          onClick={printRecommendations}
        >
          <Printer className="w-4 h-4 mr-2" />
          Imprimer
        </Button>
      </div>
      
      {/* Listen to recommendations */}
      <div className="flex justify-center">
        <TextToSpeech 
          text={generateSpeechText(diagnosisResult)}
          language="fr"
          variant="button"
          size="md"
          showVoiceSelector={true}
        />
      </div>
      
      {/* Main Actions */}
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={() => {
            setStep(1);
            setDiagnosisResult(null);
          }}
        >
          Nouveau diagnostic
        </Button>
        <Button 
          className="flex-1 bg-#2E7D32 hover:bg-#1B5E20"
          onClick={() => {
            if (diagnosisResult && onComplete) {
              onComplete(diagnosisResult);
            }
          }}
          disabled={!diagnosisResult}
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Voir les produits
        </Button>
      </div>
    </div>
  );
  
  // Navigation
  const handleNext = () => {
    if (step === 1 && canProceedStep1) setStep(2);
    else if (step === 2 && canProceedStep2) {
      if (diagnoseSkin) setStep(3);
      else if (diagnoseHair || diagnoseNails) setStep(4);
      else setStep(3);
    }
    else if (step === 3) {
      if (diagnoseHair || diagnoseNails) setStep(4);
      else submitDiagnosis();
    }
    else if (step === 4) submitDiagnosis();
  };
  
  const handleBack = () => {
    if (step === 2) {
      // If parent funnel provided identification, skip back to parent (cancel)
      if (initialIdentification) onCancel?.();
      else setStep(1);
    }
    else if (step === 3) setStep(2);
    else if (step === 4) {
      if (diagnoseSkin) setStep(3);
      else setStep(2);
    }
  };
  
  const getNextDisabled = () => {
    if (step === 1) return !canProceedStep1;
    if (step === 2) return !canProceedStep2;
    if (step === 3) return !canProceedStep3();
    if (step === 4) return !canProceedStep4();
    return false;
  };

  // Generate WhatsApp message for sharing
  const generateWhatsAppMessage = () => {
    if (!diagnosisResult) return '';
    
    let message = `🏥 *PHARMACIE SADA - Recommandations Personnalisées*\n\n`;
    message += `👤 *Client:* ${clientName}\n`;
    message += `📅 *Date:* ${new Date().toLocaleDateString('fr-FR')}\n`;
    
    if (diagnosisResult.budget) {
      const budgetLabel = BUDGET_OPTIONS.find(b => b.id === diagnosisResult.budget)?.label;
      message += `💰 *Budget:* ${budgetLabel}\n`;
    }
    
    message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    
    // Collect all unique products across all zones
    const uniqueProducts = new Map(); // Map by product code/id to avoid duplicates
    const productZones = new Map(); // Track which zones each product serves
    
    // Add recommendations by zone
    diagnosisResult.recommendations?.forEach(rec => {
      message += `\n${rec.zone_label}\n`;
      if (rec.products?.length > 0) {
        rec.products.forEach(p => {
          message += `  • ${p.name} - ${p.price?.toLocaleString('fr-FR')} FCFA\n`;
          
          // Track unique products by code or name
          const productKey = p.code || p.id || p.name;
          if (!uniqueProducts.has(productKey)) {
            uniqueProducts.set(productKey, p);
            productZones.set(productKey, [rec.zone_label]);
          } else {
            productZones.get(productKey).push(rec.zone_label);
          }
        });
      } else {
        message += `  (Aucun produit recommandé)\n`;
      }
    });
    
    // Calculate real total based on unique products only
    const realTotal = Array.from(uniqueProducts.values()).reduce((sum, p) => sum + (p.price || 0), 0);
    
    message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    
    // Show products that treat multiple zones
    const multiZoneProducts = Array.from(productZones.entries()).filter(([_, zones]) => zones.length > 1);
    if (multiZoneProducts.length > 0) {
      message += `\n💡 *Produits multi-usages:*\n`;
      multiZoneProducts.forEach(([key, zones]) => {
        const product = uniqueProducts.get(key);
        message += `  • ${product.name} → traite ${zones.join(', ')}\n`;
      });
      message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    }
    
    message += `💵 *TOTAL ESTIMÉ:* ${realTotal.toLocaleString('fr-FR')} FCFA\n`;
    message += `📦 _(${uniqueProducts.size} produit${uniqueProducts.size > 1 ? 's' : ''} unique${uniqueProducts.size > 1 ? 's' : ''})_\n`;
    
    // Check budget with real total
    const budgetMap = { budget_low: 25000, budget_medium: 50000, budget_high: 100000, budget_premium: Infinity };
    const budgetMax = budgetMap[diagnosisResult.budget] || Infinity;
    if (realTotal > budgetMax) {
      message += `⚠️ _Total dépasse le budget indiqué_\n`;
    }
    
    message += `\n📍 Pharmacie Saint Ange des Amis\n`;
    message += `📞 Contact: contact-info@phciesada.org\n`;
    
    return message;
  };

  // Generate speech text for TTS
  const generateSpeechText = (result) => {
    if (!result) return '';
    
    // Calculate unique products total for speech
    const uniqueProducts = new Map();
    result.recommendations?.forEach(rec => {
      rec.products?.forEach(p => {
        const productKey = p.code || p.id || p.name;
        if (!uniqueProducts.has(productKey)) {
          uniqueProducts.set(productKey, p);
        }
      });
    });
    const realTotal = Array.from(uniqueProducts.values()).reduce((sum, p) => sum + (p.price || 0), 0);
    
    let speech = `Bonjour ${clientName || 'cher client'}. `;
    speech += `Voici vos recommandations personnalisées de la Pharmacie Saint Ange des Amis. `;
    
    // Add recommendations by zone
    result.recommendations?.forEach((rec, index) => {
      speech += `Pour ${rec.zone_label.toLowerCase().replace(/[💅💆💇🧴]/g, '').trim()}: `;
      
      if (rec.concerns?.length > 0) {
        speech += `nous avons identifié ${rec.concerns.join(', ')}. `;
      }
      
      if (rec.products?.length > 0) {
        speech += `Nous vous recommandons: `;
        rec.products.forEach((p, i) => {
          speech += `${p.name}`;
          if (p.indication) {
            speech += `, ${p.indication}`;
          }
          speech += ` à ${p.price?.toLocaleString('fr-FR')} francs CFA`;
          if (i < rec.products.length - 1) {
            speech += '; ';
          } else {
            speech += '. ';
          }
        });
      }
    });
    
    speech += `Le total estimé de vos produits est de ${realTotal.toLocaleString('fr-FR')} francs CFA, pour ${uniqueProducts.size} produit${uniqueProducts.size > 1 ? 's' : ''} unique${uniqueProducts.size > 1 ? 's' : ''}. `;
    
    // Check budget with real total
    const budgetMap = { budget_low: 25000, budget_medium: 50000, budget_high: 100000, budget_premium: Infinity };
    const budgetMax = budgetMap[result.budget] || Infinity;
    if (realTotal > budgetMax) {
      speech += `Attention, ce total dépasse votre budget initial. `;
    }
    
    speech += `Nous vous remercions de votre confiance. À bientôt à la Pharmacie Saint Ange des Amis!`;
    
    return speech;
  };

  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    const message = generateWhatsAppMessage();
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Ouverture de WhatsApp...');
  };

  // Print recommendations
  const printRecommendations = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Recommandations - ${clientName}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px; 
            max-width: 800px; 
            margin: 0 auto;
          }
          .header { 
            text-align: center; 
            border-bottom: 2px solid #0EA5E9; 
            padding-bottom: 15px; 
            margin-bottom: 20px;
          }
          .header h1 { color: #0EA5E9; margin: 0; font-size: 24px; }
          .header p { color: #666; margin: 5px 0; }
          .client-info { 
            background: #f0f9ff; 
            padding: 15px; 
            border-radius: 8px; 
            margin-bottom: 20px;
          }
          .zone { 
            margin-bottom: 20px; 
            border: 1px solid #e5e7eb; 
            border-radius: 8px;
            overflow: hidden;
          }
          .zone-header { 
            background: linear-gradient(to right, #0EA5E9, #14B8A6); 
            color: white; 
            padding: 10px 15px;
            font-weight: bold;
          }
          .zone-concerns { 
            font-size: 12px; 
            opacity: 0.9;
            margin-top: 5px;
          }
          .product { 
            display: flex; 
            justify-content: space-between; 
            padding: 10px 15px; 
            border-bottom: 1px solid #f0f0f0;
          }
          .product:last-child { border-bottom: none; }
          .product-name { font-weight: 500; }
          .product-price { color: #0EA5E9; font-weight: bold; }
          .total { 
            background: #ecfeff; 
            padding: 15px; 
            border-radius: 8px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 18px;
            margin-top: 20px;
          }
          .total-amount { color: #0EA5E9; font-weight: bold; font-size: 24px; }
          .footer { 
            text-align: center; 
            margin-top: 30px; 
            padding-top: 15px; 
            border-top: 1px solid #e5e7eb;
            color: #666;
            font-size: 12px;
          }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🏥 PHARMACIE SAINT ANGE DES AMIS</h1>
          <p>Recommandations Personnalisées</p>
        </div>
        
        <div class="client-info">
          <strong>👤 Client:</strong> ${clientName}<br>
          <strong>📅 Date:</strong> ${new Date().toLocaleDateString('fr-FR')}<br>
          ${diagnosisResult?.budget ? `<strong>💰 Budget:</strong> ${BUDGET_OPTIONS.find(b => b.id === diagnosisResult.budget)?.label}` : ''}
        </div>
        
        ${diagnosisResult?.recommendations?.map(rec => `
          <div class="zone">
            <div class="zone-header">
              ${rec.zone_label}
              ${rec.concerns?.length > 0 ? `<div class="zone-concerns">${rec.concerns.join(', ')}</div>` : ''}
            </div>
            ${rec.products?.length > 0 ? rec.products.map(p => `
              <div class="product">
                <span class="product-name">${p.name}</span>
                <span class="product-price">${p.price?.toLocaleString('fr-FR')} FCFA</span>
              </div>
            `).join('') : '<div class="product"><em>Aucun produit recommandé</em></div>'}
          </div>
        `).join('') || ''}
        
        <div class="total">
          <span>Total estimé</span>
          <span class="total-amount">${diagnosisResult?.total_recommended_value?.toLocaleString('fr-FR')} FCFA</span>
        </div>
        
        ${diagnosisResult?.unique_products_count ? `<p style="text-align: center; color: #0891b2; font-size: 12px;">${diagnosisResult.unique_products_count} produit${diagnosisResult.unique_products_count > 1 ? 's' : ''} unique${diagnosisResult.unique_products_count > 1 ? 's' : ''}</p>` : ''}
        
        ${diagnosisResult?.multi_zone_products?.length > 0 ? `
          <div style="background: #e0f2fe; padding: 10px; border-radius: 8px; margin: 10px 0;">
            <p style="font-size: 11px; font-weight: bold; color: #0891b2; margin-bottom: 5px;">💡 Produits multi-usages :</p>
            ${diagnosisResult.multi_zone_products.map(mp => `
              <p style="font-size: 11px; color: #0e7490; margin: 2px 0;">• ${mp.name} → traite ${mp.zones?.join(', ')}</p>
            `).join('')}
          </div>
        ` : ''}
        
        ${!diagnosisResult?.within_budget ? '<p style="color: #f59e0b; text-align: center;">⚠️ Le total dépasse le budget indiqué</p>' : ''}
        
        <div class="footer">
          <p>📍 Pharmacie Saint Ange des Amis</p>
          <p>📧 contact-info@phciesada.org</p>
          <p>Merci de votre confiance!</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
    
    toast.success('Ouverture de l\'impression...');
  };
  
  return (
    <div className="max-w-xl mx-auto pb-6">
      <Card>
        <CardContent className="p-4 sm:p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          
          {/* Navigation buttons */}
          {step < 5 && (
            <div className="flex justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={step === 1 ? onCancel : handleBack}
                disabled={loading}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {step === 1 ? 'Annuler' : 'Retour'}
              </Button>
              
              <Button
                onClick={handleNext}
                disabled={getNextDisabled() || loading}
                className="bg-#2E7D32 hover:bg-#1B5E20"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyse...
                  </>
                ) : step === 4 || (step === 3 && !diagnoseHair && !diagnoseNails) ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analyser
                  </>
                ) : (
                  <>
                    Suivant
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ComprehensiveDiagnosis;
